import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"
import {
  initialFileTableState,
  initialKasouJohoState,
  initialKijunKanwaState,
  initialSyakenSyoState,
  initialSyaryoBunruiTableState,
  initialSyaryoTokusyuTableState,
  TFile,
  TFileTable,
  TKasouJoho,
  TKijunKanwa,
  TSyaryoBunrui,
  TSyaryoBunruiTable,
  TSyaryoMasterSyakenSyo,
  TSyaryoTokusyu,
  TSyaryoTokusyuTable,
} from "./baseData"
import {
  initialPictureTableState,
  TPictureFile,
  TPictureTable,
} from "./pictureData"
import {
  initialSyaryoIdoRirekiTableState,
  TSyaryoIdoRireki,
  TSyaryoIdoRirekiTable,
} from "./syaryoRirekiData"

// immutable
import { copyObject } from "@grapecity/wijmo.grid.immutable"

import {
  THyojunKoukan,
  TKyuyuCardData,
  TKyuyuCardTable,
  TSyasaiData,
  TSyasaiDataTable,
  initialKyuyuCardDataTableState,
  initialSyasaiDataTableState,
  initialETCState,
  initialHyojunKoukanState,
  TEtcCardData,
  TEtcCardTable,
  TETC,
  initialEtcCardDataTableState,
} from "./syasaiData"
import {
  initialTenkenSeibiTableState,
  TTenkenSeibi,
  TTenkenSeibiTable,
} from "./tenkenSeibiData"
import commonUtil from "../../../ts/util/commonUtil"
import { TSyaryoMasterGetRes } from "../../../service/truck/TSyaryo"
import SyaryoService from "../../../service/truck/syaryoService"

/**
 * 車両マスタ型定義
 */
export type TSyaryoMasterState = Partial<{
  車番: string
  社員コード: string
  社員氏名: string
  会社コード: string | null
  営業所コード: string | null
  部所コード: string | null
  登録番号地域: string
  登録番号分類: string
  登録番号記号: string
  登録番号指定番号: string
  登録年月日: string | null
  初年度登録年月: string | null
  廃車予定?: string | null | undefined
  廃車年月?: string | null | undefined
  廃車保管フラグ: number | null
  原動機有無フラグ: number | null
  予備車フラグ: number | null
  借用車フラグ: number | null
  車種区分コード: string | null
  車種詳細: string
  トレーラ車輌ID?: number
  トレーラ車番: string
  トレーラ車種区分名称1: string
  稼働手当車種掛率区分コード?: string | null
  変更前車番: string
  変更前登録番号地域: string
  変更前登録番号分類: string
  変更前登録番号記号: string
  変更前登録番号指定番号: string
  車番変更備考: string
  更新日時: string
  削除フラグ: number
}> &
  // 基本データ
  TSyaryoMasterSyakenSyo &
  TKijunKanwa &
  TKasouJoho &
  TSyaryoBunruiTable &
  TSyaryoTokusyuTable &
  TFileTable &
  // 車載データ
  TSyasaiDataTable &
  THyojunKoukan &
  TETC &
  TEtcCardTable &
  TKyuyuCardTable &
  // 車輌写真
  TPictureTable &
  // 点検整備履歴
  TTenkenSeibiTable &
  // 車輌異動履歴
  TSyaryoIdoRirekiTable

export const getSyaryo = createAsyncThunk<TSyaryoMasterGetRes, string, {}>(
  "syaryoMaster/getSyaryo",
  async (id: string) => {
    // 車輌取得
    try {
      const res = await new SyaryoService().get(id)
      return res.data
    } catch (error) {
      throw error
    }
  }
)

/**
 * 車両マスタ初期値
 */
export const initialState: TSyaryoMasterState = {
  車番: "",
  社員コード: "",
  社員氏名: "",
  会社コード: "",
  営業所コード: "",
  部所コード: "",
  登録番号地域: "",
  登録番号分類: "",
  登録番号記号: "",
  登録番号指定番号: "",
  登録年月日: "", //new Date().toISOString(),
  初年度登録年月: "", //new Date().toISOString(),
  廃車予定: "", //new Date().toISOString(),
  廃車年月: "", //new Date().toISOString(),
  廃車保管フラグ: 0,
  原動機有無フラグ: 0,
  予備車フラグ: 0,
  借用車フラグ: 0,
  車種区分コード: "",
  トレーラ車番: "",
  トレーラ車種区分名称1: "",
  稼働手当車種掛率区分コード: "",
  変更前車番: "",
  変更前登録番号地域: "",
  変更前登録番号分類: "",
  変更前登録番号記号: "",
  変更前登録番号指定番号: "",
  車番変更備考: "",
  // 基本データ
  車検満了日: "", //new Date().toISOString(),
  基準緩和有効期限: "", //new Date().toISOString(),
  ...initialSyakenSyoState,
  ...initialKijunKanwaState,
  ...initialKasouJohoState,
  ...initialSyaryoBunruiTableState,
  ...initialSyaryoTokusyuTableState,
  ...initialFileTableState,
  // 車載データ
  標準交換距離オイル: "",
  標準交換距離エレメント: "",
  ETC車載器メーカー: "",
  ETC車載器シリアルNO: "",
  ...initialSyasaiDataTableState,
  ...initialEtcCardDataTableState,
  ...initialKyuyuCardDataTableState,
  ...initialHyojunKoukanState,
  ...initialETCState,
  // 写真
  ...initialPictureTableState,
  // 点検整備
  ...initialTenkenSeibiTableState,
  // 車輌異動
  ...initialSyaryoIdoRirekiTableState,
  更新日時: new Date().toISOString(),
  削除フラグ: 0,
}

export const syaryoMasterSlice = createSlice({
  name: "syaryoMaster",
  initialState,
  reducers: {
    init: (state) => {
      return initialState
    },
    set: (state, action: PayloadAction<TSyaryoMasterState>) => {
      return { ...state, ...action.payload }
    },
    // 基本データ
    set車番: (state, action: PayloadAction<TSyaryoMasterState["車番"]>) => {
      return { ...state, 車番: action.payload }
    },
    set社員コード: (
      state,
      action: PayloadAction<TSyaryoMasterState["社員コード"]>
    ) => {
      return { ...state, 社員コード: action.payload }
    },
    set社員氏名: (
      state,
      action: PayloadAction<TSyaryoMasterState["社員氏名"]>
    ) => {
      return { ...state, 社員氏名: action.payload }
    },
    set会社コード: (
      state,
      action: PayloadAction<TSyaryoMasterState["会社コード"]>
    ) => {
      return { ...state, 会社コード: action.payload }
    },
    set営業所コード: (
      state,
      action: PayloadAction<TSyaryoMasterState["営業所コード"]>
    ) => {
      return { ...state, 営業所コード: action.payload }
    },
    set部所コード: (
      state,
      action: PayloadAction<TSyaryoMasterState["部所コード"]>
    ) => {
      return { ...state, 部所コード: action.payload }
    },
    set登録番号_地域名: (
      state,
      action: PayloadAction<TSyaryoMasterState["登録番号地域"]>
    ) => {
      return { ...state, 登録番号地域: action.payload }
    },
    set登録番号_分類番号: (
      state,
      action: PayloadAction<TSyaryoMasterState["登録番号分類"]>
    ) => {
      return { ...state, 登録番号分類: action.payload }
    },
    set登録番号_平仮名等: (
      state,
      action: PayloadAction<TSyaryoMasterState["登録番号記号"]>
    ) => {
      return { ...state, 登録番号記号: action.payload }
    },
    set登録番号_一連指定番号: (
      state,
      action: PayloadAction<TSyaryoMasterState["登録番号指定番号"]>
    ) => {
      return { ...state, 登録番号指定番号: action.payload }
    },
    set登録年月日: (
      state,
      action: PayloadAction<TSyaryoMasterState["登録年月日"]>
    ) => {
      return { ...state, 登録年月日: action.payload }
    },
    set初年度登録年月: (
      state,
      action: PayloadAction<TSyaryoMasterState["初年度登録年月"]>
    ) => {
      return { ...state, 初年度登録年月: action.payload }
    },
    set廃車予定: (
      state,
      action: PayloadAction<TSyaryoMasterState["廃車予定"]>
    ) => {
      return { ...state, 廃車予定: action.payload }
    },
    set廃車年月: (
      state,
      action: PayloadAction<TSyaryoMasterState["廃車年月"]>
    ) => {
      return { ...state, 廃車年月: action.payload }
    },
    set廃車保管フラグ: (
      state,
      action: PayloadAction<TSyaryoMasterState["廃車保管フラグ"]>
    ) => {
      return { ...state, 廃車保管フラグ: action.payload }
    },
    set原動機有無フラグ: (
      state,
      action: PayloadAction<TSyaryoMasterState["原動機有無フラグ"]>
    ) => {
      return { ...state, 原動機有無フラグ: action.payload }
    },
    set予備車フラグ: (
      state,
      action: PayloadAction<TSyaryoMasterState["予備車フラグ"]>
    ) => {
      return { ...state, 予備車フラグ: action.payload }
    },
    set借用車フラグ: (
      state,
      action: PayloadAction<TSyaryoMasterState["借用車フラグ"]>
    ) => {
      return { ...state, 借用車フラグ: action.payload }
    },
    set車種コード: (
      state,
      action: PayloadAction<TSyaryoMasterState["車種区分コード"]>
    ) => {
      return { ...state, 車種区分コード: action.payload }
    },
    set車種詳細: (
      state,
      action: PayloadAction<TSyaryoMasterState["車種詳細"]>
    ) => {
      return { ...state, 車種詳細: action.payload }
    },
    setトレーラ車輌ID: (
      state,
      action: PayloadAction<TSyaryoMasterState["トレーラ車輌ID"]>
    ) => {
      return { ...state, トレーラ車輌ID: action.payload }
    },
    setトレーラ車番: (
      state,
      action: PayloadAction<TSyaryoMasterState["トレーラ車番"]>
    ) => {
      return { ...state, トレーラ車番: action.payload }
    },
    setトレーラ車種: (
      state,
      action: PayloadAction<TSyaryoMasterState["トレーラ車種区分名称1"]>
    ) => {
      return { ...state, トレーラ車種区分名称1: action.payload }
    },
    set稼働手当車種掛率区分コード: (
      state,
      action: PayloadAction<TSyaryoMasterState["稼働手当車種掛率区分コード"]>
    ) => {
      return { ...state, 稼働手当車種掛率区分コード: action.payload }
    },
    set変更前車番: (
      state,
      action: PayloadAction<TSyaryoMasterState["変更前車番"]>
    ) => {
      return { ...state, 変更前車番: action.payload }
    },
    set変更前登録番号_地域名: (
      state,
      action: PayloadAction<TSyaryoMasterState["変更前登録番号地域"]>
    ) => {
      return { ...state, 変更前登録番号地域: action.payload }
    },
    set変更前登録番号_分類番号: (
      state,
      action: PayloadAction<TSyaryoMasterState["変更前登録番号分類"]>
    ) => {
      return { ...state, 変更前登録番号分類: action.payload }
    },
    set変更前登録番号_平仮名等: (
      state,
      action: PayloadAction<TSyaryoMasterState["変更前登録番号記号"]>
    ) => {
      return { ...state, 変更前登録番号記号: action.payload }
    },
    set変更前登録番号_一連指定番号: (
      state,
      action: PayloadAction<TSyaryoMasterState["変更前登録番号指定番号"]>
    ) => {
      return { ...state, 変更前登録番号指定番号: action.payload }
    },
    set備考: (
      state,
      action: PayloadAction<TSyaryoMasterState["車番変更備考"]>
    ) => {
      return { ...state, 車番変更備考: action.payload }
    },
    // 車検証
    set自動車種別コード: (
      state,
      action: PayloadAction<TSyaryoMasterState["自動車種別コード"]>
    ) => {
      return { ...state, 自動車種別コード: action.payload }
    },
    set自動車用途コード: (
      state,
      action: PayloadAction<TSyaryoMasterState["自動車用途コード"]>
    ) => {
      return { ...state, 自動車用途コード: action.payload }
    },
    set陸運支局報告分類区分コード: (
      state,
      action: PayloadAction<TSyaryoMasterState["陸運支局報告分類区分コード"]>
    ) => {
      return { ...state, 陸運支局報告分類区分コード: action.payload }
    },
    set車体形状: (
      state,
      action: PayloadAction<TSyaryoMasterState["車体形状"]>
    ) => {
      return { ...state, 車体形状: action.payload }
    },
    set支払先ID: (
      state,
      action: PayloadAction<TSyaryoMasterState["支払先ID"]>
    ) => {
      return { ...state, 支払先ID: action.payload }
    },
    setメーカーコード: (
      state,
      action: PayloadAction<TSyaryoMasterState["メーカーコード"]>
    ) => {
      return { ...state, メーカーコード: action.payload }
    },
    setメーカー車名: (
      state,
      action: PayloadAction<TSyaryoMasterState["メーカー車名"]>
    ) => {
      return { ...state, メーカー車名: action.payload }
    },
    set乗車定員: (
      state,
      action: PayloadAction<TSyaryoMasterState["乗車定員"]>
    ) => {
      return { ...state, 乗車定員: action.payload }
    },
    set最大積載量: (
      state,
      action: PayloadAction<TSyaryoMasterState["最大積載量"]>
    ) => {
      return { ...state, 最大積載量: action.payload }
    },
    set最大積載量2: (
      state,
      action: PayloadAction<TSyaryoMasterState["最大積載量2"]>
    ) => {
      return { ...state, 最大積載量2: action.payload }
    },
    set車輌重量: (
      state,
      action: PayloadAction<TSyaryoMasterState["車輌重量"]>
    ) => {
      return { ...state, 車輌重量: action.payload }
    },
    set車輌総重量: (
      state,
      action: PayloadAction<TSyaryoMasterState["車輌総重量"]>
    ) => {
      return { ...state, 車輌総重量: action.payload }
    },
    set車台番号: (
      state,
      action: PayloadAction<TSyaryoMasterState["車台番号"]>
    ) => {
      return { ...state, 車台番号: action.payload }
    },
    set長さ: (state, action: PayloadAction<TSyaryoMasterState["長さ"]>) => {
      return { ...state, 長さ: action.payload }
    },
    set幅: (state, action: PayloadAction<TSyaryoMasterState["幅"]>) => {
      return { ...state, 幅: action.payload }
    },
    set高さ: (state, action: PayloadAction<TSyaryoMasterState["高さ"]>) => {
      return { ...state, 高さ: action.payload }
    },
    setシリアルNo: (
      state,
      action: PayloadAction<TSyaryoMasterState["シリアルNo"]>
    ) => {
      return { ...state, シリアルNo: action.payload }
    },
    set車輌形式: (
      state,
      action: PayloadAction<TSyaryoMasterState["車輌形式"]>
    ) => {
      return { ...state, 車輌形式: action.payload }
    },
    set前前軸重: (
      state,
      action: PayloadAction<TSyaryoMasterState["前前軸重"]>
    ) => {
      return { ...state, 前前軸重: action.payload }
    },
    set前後軸重: (
      state,
      action: PayloadAction<TSyaryoMasterState["前後軸重"]>
    ) => {
      return { ...state, 前後軸重: action.payload }
    },
    set後前軸重: (
      state,
      action: PayloadAction<TSyaryoMasterState["後前軸重"]>
    ) => {
      return { ...state, 後前軸重: action.payload }
    },
    set後後軸重: (
      state,
      action: PayloadAction<TSyaryoMasterState["後後軸重"]>
    ) => {
      return { ...state, 後後軸重: action.payload }
    },
    set原動機型式: (
      state,
      action: PayloadAction<TSyaryoMasterState["原動機型式"]>
    ) => {
      return { ...state, 原動機型式: action.payload }
    },
    set所有者名称: (
      state,
      action: PayloadAction<TSyaryoMasterState["所有者名称"]>
    ) => {
      return { ...state, 所有者名称: action.payload }
    },
    set車検満了日: (
      state,
      action: PayloadAction<TSyaryoMasterState["車検満了日"]>
    ) => {
      return { ...state, 車検満了日: action.payload }
    },
    set使用者名称: (
      state,
      action: PayloadAction<TSyaryoMasterState["使用者名称"]>
    ) => {
      return { ...state, 使用者名称: action.payload }
    },
    set車輌営業所: (
      state,
      action: PayloadAction<TSyaryoMasterState["車輌営業所"]>
    ) => {
      return { ...state, 車輌営業所: action.payload }
    },
    // 基準緩和
    set基準緩和車輌: (
      state,
      action: PayloadAction<TSyaryoMasterState["基準緩和車輌"]>
    ) => {
      return { ...state, 基準緩和車輌: action.payload }
    },
    set基準緩和有効期限: (
      state,
      action: PayloadAction<TSyaryoMasterState["基準緩和有効期限"]>
    ) => {
      return { ...state, 基準緩和有効期限: action.payload }
    },
    set緩和書類制作会社: (
      state,
      action: PayloadAction<TSyaryoMasterState["緩和書類制作会社"]>
    ) => {
      return { ...state, 緩和書類制作会社: action.payload }
    },
    set緩和輸送依頼荷主: (
      state,
      action: PayloadAction<TSyaryoMasterState["緩和輸送依頼荷主"]>
    ) => {
      return { ...state, 緩和輸送依頼荷主: action.payload }
    },
    // 架装情報
    set架装メーカー: (
      state,
      action: PayloadAction<TSyaryoMasterState["架装メーカー"]>
    ) => {
      return { ...state, 架装メーカー: action.payload }
    },
    set架装タンクNo: (
      state,
      action: PayloadAction<TSyaryoMasterState["架装タンクNO"]>
    ) => {
      return { ...state, 架装タンクNO: action.payload }
    },
    set台帳保管ファイル名: (
      state,
      action: PayloadAction<TSyaryoMasterState["台帳保管ファイル名"]>
    ) => {
      return { ...state, 台帳保管ファイル名: action.payload }
    },
    set架装情報備考1: (
      state,
      action: PayloadAction<TSyaryoMasterState["備考１"]>
    ) => {
      return { ...state, 備考１: action.payload }
    },
    set架装情報備考2: (
      state,
      action: PayloadAction<TSyaryoMasterState["備考２"]>
    ) => {
      return { ...state, 備考２: action.payload }
    },
    set任意保険加入フラグ: (
      state,
      action: PayloadAction<TSyaryoMasterState["任意保険加入フラグ"]>
    ) => {
      return { ...state, 任意保険加入フラグ: action.payload }
    },
    set火炎暴露保険フラグ: (
      state,
      action: PayloadAction<TSyaryoMasterState["火炎暴露保険フラグ"]>
    ) => {
      return { ...state, 火炎暴露保険フラグ: action.payload }
    },
    set保険No: (state, action: PayloadAction<TSyaryoMasterState["保険NO"]>) => {
      return { ...state, 保険NO: action.payload }
    },

    // 固定項目
    set更新日時: (
      state,
      action: PayloadAction<TSyaryoMasterState["更新日時"]>
    ) => {
      return { ...state, 更新日時: action.payload }
    },
    set削除フラグ: (
      state,
      action: PayloadAction<TSyaryoMasterState["削除フラグ"]>
    ) => {
      return { ...state, 削除フラグ: action.payload }
    },

    // 車輌分類
    set車輌分類テーブル: (state, action: PayloadAction<TSyaryoBunrui[]>) => {
      //  uniqueIDをセットする為、リストをループして設定
      let newItemList: TSyaryoBunrui[] = []
      action.payload.forEach((d) => {
        let newItem = Object.freeze(
          copyObject({}, { ...d, row_id: commonUtil.getUniqueStr() })
        )
        newItemList.push(newItem)
      })

      return copyObject({}, state, {
        車輌分類: [...newItemList],
      })
    },
    set車輌分類テーブルRow: (
      state,
      action: PayloadAction<{ newItem: TSyaryoBunrui }>
    ) => {
      // 変更前データリストから変更行のrow_idと一致するデータ行を抽出。
      let items = state.車輌分類,
        rowId = action.payload.newItem.row_id,
        index = items.findIndex((i) => i.row_id === rowId),
        oldItem = items[index]
      // エラー回避のおまじない
      if (rowId == null || index < 0) return state

      // 編集データをコピーする。
      let clonedItem = Object.freeze(
        copyObject({}, oldItem, action.payload.newItem)
      )
      // データリストに編集データを差し込む。
      return copyObject({}, state, {
        車輌分類: items
          .slice(0, index)
          .concat([clonedItem])
          .concat(items.slice(index + 1)),
      })
    },
    add車輌分類テーブル: (
      state,
      action: PayloadAction<{ newItem: TSyaryoBunrui }>
    ) => {
      let newItem = Object.freeze(
        copyObject(
          {},
          { ...action.payload.newItem, row_id: commonUtil.getUniqueStr() }
        )
      )

      return copyObject({}, state, {
        車輌分類: state.車輌分類.concat([newItem]),
      })
    },
    remove車輌分類テーブル: (
      state,
      action: PayloadAction<{ rowId: string }>
    ) => {
      let items = state.車輌分類,
        rowId = action.payload.rowId
      const index = items.findIndex((i) => i.row_id === rowId)

      if (rowId == null || index < 0) return state
      return copyObject({}, state, {
        車輌分類: items.slice(0, index).concat(items.slice(index + 1)),
      })
    },
    // 車輌特種
    set車輌特種テーブル: (state, action: PayloadAction<TSyaryoTokusyu[]>) => {
      //  uniqueIDをセットする為、リストをループして設定
      let newItemList: TSyaryoTokusyu[] = []
      action.payload.forEach((d) => {
        let newItem = Object.freeze(
          copyObject({}, { ...d, row_id: commonUtil.getUniqueStr() })
        )
        newItemList.push(newItem)
      })

      return copyObject({}, state, {
        車輌特種: [...newItemList],
      })
    },
    set車輌特種テーブルRow: (
      state,
      action: PayloadAction<{ newItem: TSyaryoTokusyu }>
    ) => {
      // 変更前データリストから変更行のrow_idと一致するデータ行を抽出。
      let items = state.車輌特種,
        rowId = action.payload.newItem.row_id,
        index = items.findIndex((i) => i.row_id === rowId),
        oldItem = items[index]
      // エラー回避のおまじない
      if (rowId == null || index < 0) return state

      // 編集データをコピーする。
      let clonedItem = Object.freeze(
        copyObject({}, oldItem, action.payload.newItem)
      )
      // データリストに編集データを差し込む。
      return copyObject({}, state, {
        車輌特種: items
          .slice(0, index)
          .concat([clonedItem])
          .concat(items.slice(index + 1)),
      })
    },
    add車輌特種テーブル: (
      state,
      action: PayloadAction<{ newItem: TSyaryoTokusyu }>
    ) => {
      let newItem = Object.freeze(
        copyObject(
          {},
          { ...action.payload.newItem, row_id: commonUtil.getUniqueStr() }
        )
      )

      return copyObject({}, state, {
        車輌特種: state.車輌特種.concat([newItem]),
      })
    },
    remove車輌特種テーブル: (
      state,
      action: PayloadAction<{ rowId: string }>
    ) => {
      let items = state.車輌特種,
        rowId = action.payload.rowId
      const index = items.findIndex((i) => i.row_id === rowId)

      if (rowId == null || index < 0) return state
      return copyObject({}, state, {
        車輌特種: items.slice(0, index).concat(items.slice(index + 1)),
      })
    },
    // 車載データ
    set車載データテーブル: (state, action: PayloadAction<TSyasaiData[]>) => {
      //  uniqueIDをセットする為、リストをループして設定
      let newItemList: TSyasaiData[] = []
      action.payload.forEach((d) => {
        let newItem = Object.freeze(
          copyObject({}, { ...d, row_id: commonUtil.getUniqueStr() })
        )
        newItemList.push(newItem)
      })

      return copyObject({}, state, {
        車輌車載リスト: [...newItemList],
      })
    },
    set車載データテーブルRow: (
      state,
      action: PayloadAction<{ newItem: TSyasaiData }>
    ) => {
      // 変更前データリストから変更行のrow_idと一致するデータ行を抽出。
      let items = state.車輌車載リスト,
        rowId = action.payload.newItem.row_id,
        index = items.findIndex((i) => i.row_id === rowId),
        oldItem = items[index]
      // エラー回避のおまじない
      if (rowId == null || index < 0) return state

      // 編集データをコピーする。
      let clonedItem = Object.freeze(
        copyObject({}, oldItem, action.payload.newItem)
      )
      // データリストに編集データを差し込む。
      return copyObject({}, state, {
        車輌車載リスト: items
          .slice(0, index)
          .concat([clonedItem])
          .concat(items.slice(index + 1)),
      })
    },
    add車載データテーブル: (
      state,
      action: PayloadAction<{ newItem: TSyasaiData }>
    ) => {
      let newItem = Object.freeze(
        copyObject(
          {},
          { ...action.payload.newItem, row_id: commonUtil.getUniqueStr() }
        )
      )

      return copyObject({}, state, {
        車輌車載リスト: state.車輌車載リスト.concat([newItem]),
      })
    },
    remove車載データテーブル: (
      state,
      action: PayloadAction<{ rowId: string }>
    ) => {
      let items = state.車輌車載リスト,
        rowId = action.payload.rowId
      const index = items.findIndex((i) => i.row_id === rowId)

      if (rowId == null || index < 0) return state
      return copyObject({}, state, {
        車輌車載リスト: items.slice(0, index).concat(items.slice(index + 1)),
      })
    },

    set標準交換距離オイル: (
      state,
      action: PayloadAction<THyojunKoukan["標準交換距離オイル"]>
    ) => {
      return { ...state, 標準交換距離オイル: action.payload }
    },
    set標準交換距離エレメント: (
      state,
      action: PayloadAction<THyojunKoukan["標準交換距離エレメント"]>
    ) => {
      return { ...state, 標準交換距離エレメント: action.payload }
    },
    setETC設置区分: (state, action: PayloadAction<TETC["ETC設置区分"]>) => {
      return { ...state, ETC設置区分: action.payload }
    },
    setETC車載器メーカー: (
      state,
      action: PayloadAction<TETC["ETC車載器メーカー"]>
    ) => {
      return { ...state, ETC車載器メーカー: action.payload }
    },
    setETC車載器シリアルNO: (
      state,
      action: PayloadAction<TETC["ETC車載器シリアルNO"]>
    ) => {
      return { ...state, ETC車載器シリアルNO: action.payload }
    },
    // ETCカード
    setETCカードテーブル: (state, action: PayloadAction<TEtcCardData[]>) => {
      //  uniqueIDをセットする為、リストをループして設定
      let newItemList: TEtcCardData[] = []
      action.payload.forEach((d) => {
        let newItem = Object.freeze(
          copyObject({}, { ...d, row_id: commonUtil.getUniqueStr() })
        )
        newItemList.push(newItem)
      })

      return copyObject({}, state, {
        ETCカードリスト: [...newItemList],
      })
    },
    setETCカードテーブルRow: (
      state,
      action: PayloadAction<{ newItem: TEtcCardData }>
    ) => {
      // 変更前データリストから変更行のrow_idと一致するデータ行を抽出。
      let items = state.ETCカードリスト,
        rowId = action.payload.newItem.row_id,
        index = items.findIndex((i) => i.row_id === rowId),
        oldItem = items[index]
      // エラー回避のおまじない
      if (rowId == null || index < 0) return state

      // 編集データをコピーする。
      let clonedItem = Object.freeze(
        copyObject({}, oldItem, action.payload.newItem)
      )
      // データリストに編集データを差し込む。
      return copyObject({}, state, {
        ETCカードリスト: items
          .slice(0, index)
          .concat([clonedItem])
          .concat(items.slice(index + 1)),
      })
    },
    addETCカードテーブル: (
      state,
      action: PayloadAction<{ newItem: TEtcCardData }>
    ) => {
      let newItem = Object.freeze(
        copyObject(
          {},
          { ...action.payload.newItem, row_id: commonUtil.getUniqueStr() }
        )
      )

      return copyObject({}, state, {
        ETCカードリスト: state.ETCカードリスト.concat([newItem]),
      })
    },
    removeETCカードテーブル: (
      state,
      action: PayloadAction<{ rowId: string }>
    ) => {
      let items = state.ETCカードリスト,
        rowId = action.payload.rowId
      const index = items.findIndex((i) => i.row_id === rowId)

      if (rowId == null || index < 0) return state
      return copyObject({}, state, {
        ETCカードリスト: items.slice(0, index).concat(items.slice(index + 1)),
      })
    },

    // 給油カード
    set給油カードテーブル: (state, action: PayloadAction<TKyuyuCardData[]>) => {
      //  uniqueIDをセットする為、リストをループして設定
      let newItemList: TKyuyuCardData[] = []
      action.payload.forEach((d) => {
        let newItem = Object.freeze(
          copyObject({}, { ...d, row_id: commonUtil.getUniqueStr() })
        )
        newItemList.push(newItem)
      })

      return copyObject({}, state, {
        給油カードリスト: [...newItemList],
      })
    },
    set給油カードテーブルRow: (
      state,
      action: PayloadAction<{ newItem: TKyuyuCardData }>
    ) => {
      // 変更前データリストから変更行のrow_idと一致するデータ行を抽出。
      let items = state.給油カードリスト,
        rowId = action.payload.newItem.row_id,
        index = items.findIndex((i) => i.row_id === rowId),
        oldItem = items[index]
      // エラー回避のおまじない
      if (rowId == null || index < 0) return state

      // 編集データをコピーする。
      let clonedItem = Object.freeze(
        copyObject({}, oldItem, action.payload.newItem)
      )
      // データリストに編集データを差し込む。
      return copyObject({}, state, {
        給油カードリスト: items
          .slice(0, index)
          .concat([clonedItem])
          .concat(items.slice(index + 1)),
      })
    },
    add給油カードテーブル: (
      state,
      action: PayloadAction<{ newItem: TKyuyuCardData }>
    ) => {
      let newItem = Object.freeze(
        copyObject(
          {},
          { ...action.payload.newItem, row_id: commonUtil.getUniqueStr() }
        )
      )

      return copyObject({}, state, {
        給油カードリスト: state.給油カードリスト.concat([newItem]),
      })
    },
    remove給油カードテーブル: (
      state,
      action: PayloadAction<{ rowId: string }>
    ) => {
      let items = state.給油カードリスト,
        rowId = action.payload.rowId
      const index = items.findIndex((i) => i.row_id === rowId)

      if (rowId == null || index < 0) return state
      return copyObject({}, state, {
        給油カードリスト: items.slice(0, index).concat(items.slice(index + 1)),
      })
    },

    // 添付ファイル
    set添付ファイル: (state, action: PayloadAction<TFile | TFile[]>) => {
      if (Array.isArray(action.payload)) {
        return { ...state, 添付ファイルリスト: [...action.payload] }
      }
      return { ...state, 添付ファイルリスト: [action.payload] }
    },
    add添付ファイル: (state, action: PayloadAction<TFile | TFile[]>) => {
      if (Array.isArray(action.payload)) {
        return {
          ...state,
          添付ファイルリスト: [...state.添付ファイルリスト, ...action.payload],
        }
      }
      return {
        ...state,
        添付ファイルリスト: [...state.添付ファイルリスト, action.payload],
      }
    },
    edit添付ファイル: (state, action: PayloadAction<TFile>) => {
      const newArray = [...state.添付ファイルリスト]

      const index = newArray.findIndex(
        (item) => item.row_id === action.payload.row_id
      )
      if (index !== -1) {
        newArray.splice(index, 1, { ...action.payload })
      }
      return { ...state, 添付ファイルリスト: [...newArray] }
    },
    remove添付ファイル: (state, action: PayloadAction<TFile>) => {
      return {
        ...state,
        添付ファイルリスト: [
          ...state.添付ファイルリスト.filter(
            (i) => i.row_id !== action.payload.row_id
          ),
        ],
      }
    },

    // 車輌写真
    set車輌写真: (
      state,
      action: PayloadAction<TPictureFile | TPictureFile[]>
    ) => {
      if (Array.isArray(action.payload)) {
        return { ...state, 車輌写真リスト: [...action.payload] }
      }
      return { ...state, 車輌写真リスト: [action.payload] }
    },
    add車輌写真: (
      state,
      action: PayloadAction<TPictureFile | TPictureFile[]>
    ) => {
      if (Array.isArray(action.payload)) {
        return {
          ...state,
          車輌写真リスト: [...state.車輌写真リスト, ...action.payload],
        }
      }
      return {
        ...state,
        車輌写真リスト: [...state.車輌写真リスト, action.payload],
      }
    },
    edit車輌写真: (state, action: PayloadAction<TPictureFile>) => {
      const newArray = [...state.車輌写真リスト]

      const index = newArray.findIndex(
        (item) => item.row_id === action.payload.row_id
      )
      if (index !== -1) {
        newArray.splice(index, 1, { ...action.payload })
      }
      return { ...state, 車輌写真リスト: [...newArray] }
    },
    remove車輌写真: (state, action: PayloadAction<TPictureFile>) => {
      return {
        ...state,
        車輌写真リスト: [
          ...state.車輌写真リスト.filter(
            (i) => i.row_id !== action.payload.row_id
          ),
        ],
      }
    },
    // 点検整備履歴
    set点検整備履歴: (state, action: PayloadAction<TTenkenSeibi[]>) => {
      //  uniqueIDをセットする為、リストをループして設定
      let newItemList: TTenkenSeibi[] = []
      action.payload.forEach((d) => {
        let newItem = Object.freeze(
          copyObject({}, { ...d, row_id: commonUtil.getUniqueStr() })
        )
        newItemList.push(newItem)
      })

      return copyObject({}, state, {
        点検整備一覧: [...newItemList],
      })
    },

    // 車輌異動履歴
    set車輌異動履歴: (state, action: PayloadAction<TSyaryoIdoRireki[]>) => {
      //  uniqueIDをセットする為、リストをループして設定
      let newItemList: TSyaryoIdoRireki[] = []
      action.payload.forEach((d) => {
        let newItem = Object.freeze(
          copyObject({}, { ...d, row_id: commonUtil.getUniqueStr() })
        )
        newItemList.push(newItem)
      })

      return copyObject({}, state, {
        車輌所属リスト: [...newItemList],
      })
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      getSyaryo.fulfilled,
      (state: TSyaryoMasterState, action) => {
        state.更新日時 = action.payload.更新日時
        state.削除フラグ = action.payload.削除フラグ
        state.車番 = action.payload.車番
        state.部所コード = action.payload.部所コード
        state.社員コード = action.payload.社員コード
        state.社員氏名 = action.payload.社員氏名
        state.廃車予定 = action.payload.廃車予定年月
        state.登録番号地域 = action.payload.登録番号地域
        state.登録番号分類 = action.payload.登録番号分類
        state.登録番号記号 = action.payload.登録番号記号
        state.登録番号指定番号 = action.payload.登録番号指定番号
        state.登録年月日 = action.payload.登録年月日
        state.初年度登録年月 = action.payload.初年度登録年月
        state.廃車年月 = action.payload.廃車年月
        state.廃車保管フラグ = action.payload.廃車保管フラグ
        state.原動機有無フラグ = action.payload.原動機有無フラグ
        state.予備車フラグ = action.payload.予備車フラグ
        state.借用車フラグ = action.payload.借用車フラグ
        state.車種区分コード = action.payload.車種区分コード
        state.稼働手当車種掛率区分コード =
          action.payload.稼働手当車種掛率区分コード
        state.トレーラ車輌ID = action.payload.トレーラ車輌ID
        state.トレーラ車番 = action.payload.トレーラ車番
        state.トレーラ車種区分名称1 = action.payload.トレーラ車種区分名称1
        state.変更前車番 = action.payload.変更前車番
        state.変更前登録番号地域 = action.payload.変更前登録番号地域
        state.変更前登録番号分類 = action.payload.変更前登録番号分類
        state.変更前登録番号記号 = action.payload.変更前登録番号記号
        state.変更前登録番号指定番号 = action.payload.変更前登録番号指定番号
        state.車番変更備考 = action.payload.車番変更備考
        state.陸運支局報告分類区分コード = action.payload.陸運支局分類区分コード
        state.車体形状 = action.payload.車体形状
        state.車種詳細 = action.payload.車種詳細
        state.メーカーコード = action.payload.メーカーコード
        state.メーカー車名 = action.payload.メーカー車名
        state.最大積載量 = action.payload.最大積載量
        state.最大積載量2 = action.payload.最大積載量2
        state.車輌重量 = action.payload.車輌重量
        state.車輌総重量 = action.payload.車輌総重量
        state.車台番号 = action.payload.車台番号
        state.長さ = action.payload.長さ
        state.幅 = action.payload.幅
        state.高さ = action.payload.高さ
        state.シリアルNo = action.payload.シリアルNO
        state.前前軸重 = action.payload.前前軸重
        state.前後軸重 = action.payload.前後軸重
        state.後前軸重 = action.payload.後前軸重
        state.後後軸重 = action.payload.後後軸重
        state.車輌形式 = action.payload.車輌形式
        state.原動機型式 = action.payload.原動機型式
        state.所有者名称 = action.payload.所有者名称
        state.車検満了日 = action.payload.車検満了日
        state.使用者名称 = action.payload.使用者情報_会社コード
        state.車輌営業所 = action.payload.車輌営業所区分コード
        state.基準緩和車輌 = action.payload.基準緩和車輌
        state.基準緩和有効期限 = action.payload.基準緩和有効期限
        state.緩和書類制作会社 = action.payload.緩和書類制作会社
        state.緩和輸送依頼荷主 = action.payload.緩和輸送依頼荷主
        state.架装メーカー = action.payload.架装メーカー
        state.架装タンクNO = action.payload.架装タンクNO
        state.台帳保管ファイル名 = action.payload.台帳保管ファイル名
        state.備考１ = action.payload.備考１
        state.備考２ = action.payload.備考２
        state.任意保険加入フラグ = action.payload.任意保険加入フラグ
        state.火炎暴露保険フラグ = action.payload.火炎暴露保険フラグ
        state.保険NO = action.payload.保険NO
        state.標準交換距離オイル = action.payload.標準交換距離オイル
        state.標準交換距離エレメント = action.payload.標準交換距離エレメント
        state.ETC設置区分 = action.payload.ETC設置区分コード
        state.ETC車載器メーカー = action.payload.ETC車載器メーカー
        state.ETC車載器シリアルNO = action.payload.ETC車載器シリアルNO
        state.車輌分類 = commonUtil.addRowId(action.payload.車輌分類リスト)
        state.車輌特種 = commonUtil.addRowId(action.payload.車輌特種リスト)
        state.添付ファイルリスト = commonUtil.addRowId(
          action.payload.添付ファイルリスト
        )
        state.車輌車載リスト = commonUtil.addRowId(
          action.payload.車輌車載リスト
        )
        state.点検整備一覧 = commonUtil.addRowId(
          action.payload.車輌点検整備リスト
        )
        state.ETCカードリスト = commonUtil.addRowId(
          action.payload.車輌ETCカードリスト
        )
        state.給油カードリスト = commonUtil.addRowId(
          action.payload.車輌給油カードリスト
        )
        state.車輌所属リスト = commonUtil.addRowId(
          action.payload.車輌所属リスト
        )
        state.車輌写真リスト = commonUtil.addRowId(
          action.payload.車輌写真リスト
        )

        return state
      }
    )
  },
})

export const syaryoMasterActions = syaryoMasterSlice.actions
